import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import API_ENDPOINT from 'constants/apiEndpoint';
import environment from 'constants/environment';

const requestConfig = axios.create({
  baseURL: environment.baseApiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

/*
 * Session tokens.
 *
 * Keys match wamo-admin-frontend (`@wamo:accessToken` / `@wamo:refreshToken`) so the two admin
 * apps name the same things identically. Kept in module state as well as `localStorage` so the
 * request interceptor does not touch storage on every call.
 */
const STORAGE_PREFIX = '@wamo:';
const ACCESS_TOKEN_KEY = `${STORAGE_PREFIX}accessToken`;
const REFRESH_TOKEN_KEY = `${STORAGE_PREFIX}refreshToken`;

const readStored = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string | null): void => {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    /* Private browsing or a full quota: the in-memory copy still carries the session. */
  }
};

let accessToken: string | null = readStored(ACCESS_TOKEN_KEY);
let refreshToken: string | null = readStored(REFRESH_TOKEN_KEY);

/** Both halves of a session, as login and refresh both return them together. */
export const setSession = (tokens: { accessToken: string; refreshToken: string }): void => {
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
  writeStored(ACCESS_TOKEN_KEY, tokens.accessToken);
  writeStored(REFRESH_TOKEN_KEY, tokens.refreshToken);
};

export const clearSession = (): void => {
  accessToken = null;
  refreshToken = null;
  writeStored(ACCESS_TOKEN_KEY, null);
  writeStored(REFRESH_TOKEN_KEY, null);
};

/** Access token only — for `VITE_ADMIN_TOKEN`, which has no refresh half. */
export const setAuthToken = (token: string | null): void => {
  accessToken = token;
  writeStored(ACCESS_TOKEN_KEY, token);
};

export const isAuthenticated = (): boolean => Boolean(accessToken);

type TRetriableConfig = InternalAxiosRequestConfig & {
  _retried?: boolean;
  /** The access token this attempt actually carried, so a 401 can tell "expired" from "stale". */
  _sentToken?: string | null;
};

requestConfig.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  (config as TRetriableConfig)._sentToken = accessToken;
  return config;
});

/*
 * A dedicated client for the refresh call, with NO interceptors: a 401 from `/v1/refresh` must not
 * re-enter the refresh path, and the request interceptor would otherwise attach the very access
 * token that just expired.
 */
const refreshClient = axios.create({
  baseURL: environment.baseApiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

/*
 * Single-flight refresh.
 *
 * The endpoint ROTATES the refresh token, so two concurrent refreshes would race: the first
 * consumes the token and the second presents a spent one, killing the session. A dashboard fires
 * several queries at once, so concurrent 401s are the normal case, not an edge case — every caller
 * awaits the same in-flight promise and they all continue on its result.
 */
let inFlightRefresh: Promise<string | null> | null = null;

const refreshAccessToken = (): Promise<string | null> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }
  const token = refreshToken;
  if (!token) {
    return Promise.resolve(null);
  }

  inFlightRefresh = refreshClient
    .post<{ result?: { accessToken?: string; refreshToken?: string } }>(API_ENDPOINT.auth.refresh, {
      // The field really is `token` — the API does not call it `refreshToken` here.
      token,
    })
    .then((response) => {
      const result = response.data?.result;
      if (!result?.accessToken || !result?.refreshToken) {
        return null;
      }
      setSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      return result.accessToken;
    })
    .catch(() => null)
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
};

const LOGIN_PATH = '/login';

const endSession = (): void => {
  clearSession();
  // Navigated with window.location rather than the router: this module sits under configs/ and
  // importing the router here would cycle back through routes -> screens -> api -> helpers/api.
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
};

requestConfig.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as TRetriableConfig | undefined;
    const url = original?.url ?? '';

    // A rejected sign-in is also a 401; LoginScreen shows its own message, and redirecting would
    // reload the page out from under the form.
    const isLoginAttempt = url.endsWith(API_ENDPOINT.auth.login);
    const isRefreshAttempt = url.endsWith(API_ENDPOINT.auth.refresh);
    const isUnauthorized = status === 401 || status === 403;

    if (!isUnauthorized || isLoginAttempt || isRefreshAttempt) {
      return Promise.reject(error);
    }

    // 403 is included because wamo-admin-frontend treats it as an expiry signal too. One attempt
    // only — `_retried` is the loop guard axios does not give us.
    if (original && !original._retried && refreshToken) {
      original._retried = true;

      /*
       * This request went out with a token that has since been replaced, so its 401 says nothing
       * about the CURRENT token — a concurrent request already refreshed. Retry straight away.
       *
       * Without this, requests whose 401 lands just after a refresh completes start a second
       * refresh cycle, and because the endpoint rotates the refresh token the later call presents a
       * spent one and takes the whole session down. The in-flight promise alone does not cover it:
       * it only dedupes failures that overlap, not ones that arrive late.
       */
      if (original._sentToken && original._sentToken !== accessToken) {
        return requestConfig(original);
      }

      const fresh = await refreshAccessToken();
      if (fresh) {
        // No need to set the header here: the request interceptor re-reads the module token.
        return requestConfig(original);
      }
      // The refresh token is spent or rejected — the session is genuinely over.
      endSession();
      return Promise.reject(error);
    }

    /*
     * No refresh possible. Only a 401 ends the session: a 403 that survives a successful refresh is
     * a permission error, not an expiry, and logging the user out for it would be wrong.
     */
    if (status === 401) {
      endSession();
    }
    return Promise.reject(error);
  }
);

export default requestConfig;
